const CATEGORY_ORDER = [
  "Foundation",
  "AI Aware",
  "AI Practitioner",
  "AI Strategist",
  "AI Native",
];

function getOrderedCategoryNames(scoreboardCoursesGroupedByCategory) {
  if (!(scoreboardCoursesGroupedByCategory instanceof Map)) {
    return [];
  }
  const availableCategories = Array.from(
    scoreboardCoursesGroupedByCategory.keys(),
  );
  const knownCategories = CATEGORY_ORDER.filter((categoryName) =>
    scoreboardCoursesGroupedByCategory.has(categoryName),
  );
  const additionalCategories = availableCategories.filter(
    (categoryName) => !CATEGORY_ORDER.includes(categoryName),
  );
  return [...knownCategories, ...additionalCategories];
}

export function computePoints(
  scoreboardCoursesGroupedByCategory,
  programPointsPerCategory,
) {
  if (
    !(scoreboardCoursesGroupedByCategory instanceof Map) ||
    !(programPointsPerCategory instanceof Map)
  ) {
    return new Map();
  }

  const completedPointsPerCategory = new Map();

  for (const [categoryName, courses] of scoreboardCoursesGroupedByCategory) {
    const categoryProgramPoints =
      programPointsPerCategory.get(categoryName) || {};

    const completedProgramPoints = {};

    if (!Array.isArray(courses)) {
      completedPointsPerCategory.set(categoryName, completedProgramPoints);

      continue;
    }

    for (const [programName, assignedPoints] of Object.entries(
      categoryProgramPoints,
    )) {
      const normalizedProgramName = programName.trim().toLowerCase();

      const programCourses = courses.filter((course) => {
        if (typeof course?.program !== "string") {
          return false;
        }

        return course.program.trim().toLowerCase() === normalizedProgramName;
      });

      const validProgramCourses = programCourses.filter((course) => {
        return (
          typeof course?.courseName === "string" &&
          course.courseName.trim() !== ""
        );
      });

      const hasValidCourse = validProgramCourses.length > 0;

      const allCoursesCompleted =
        hasValidCourse &&
        validProgramCourses.every((course) => {
          return Boolean(course.completionDate || course.programCompleted);
        });

      const numericAssignedPoints = Number(assignedPoints);

      completedProgramPoints[programName] =
        allCoursesCompleted && Number.isFinite(numericAssignedPoints)
          ? numericAssignedPoints
          : 0;
    }

    completedPointsPerCategory.set(categoryName, completedProgramPoints);
  }

  return completedPointsPerCategory;
}

export function computeScore(
  scoreboardCoursesGroupedByCategory,
  totalPointsPerCategory,
  completedPointsPerCategory,
) {
  if (
    !(scoreboardCoursesGroupedByCategory instanceof Map) ||
    !(totalPointsPerCategory instanceof Map) ||
    !(completedPointsPerCategory instanceof Map)
  ) {
    return 0;
  }

  const orderedCategoryNames = getOrderedCategoryNames(
    scoreboardCoursesGroupedByCategory,
  );

  let score = 0;

  for (const categoryName of orderedCategoryNames) {
    const totalCategoryPoints = Number(
      totalPointsPerCategory.get(categoryName),
    );

    if (!Number.isFinite(totalCategoryPoints) || totalCategoryPoints <= 0) {
      continue;
    }

    const completedProgramPoints =
      completedPointsPerCategory.get(categoryName) || {};
    const hasNoPrograms = Object.keys(completedProgramPoints).length === 0;
    const completedCategoryPoints = sumProgramPoints(completedProgramPoints);
    const isCategoryCompleted =
      hasNoPrograms || completedCategoryPoints >= totalCategoryPoints;

    if (isCategoryCompleted) {
      score += totalCategoryPoints;
      continue;
    }
    score += completedCategoryPoints;
    break;
  }

  return score;
}

export function computeLevel(score) {
  const numericScore = Number(score);

  if (!Number.isFinite(numericScore)) {
    return {
      currentLevel: "Foundation",
      nextLevel: "AI Aware",
    };
  }

  if (numericScore >= 500) {
    return {
      currentLevel: "AI Native",
      nextLevel: "",
    };
  }

  if (numericScore >= 400) {
    return {
      currentLevel: "AI Strategist",
      nextLevel: "AI Native",
    };
  }

  if (numericScore >= 300) {
    return {
      currentLevel: "AI Practitioner",
      nextLevel: "AI Strategist",
    };
  }

  if (numericScore >= 200) {
    return {
      currentLevel: "AI Aware",
      nextLevel: "AI Practitioner",
    };
  }

  return {
    currentLevel: "Foundation",
    nextLevel: "AI Aware",
  };
}

export function computeNextCategories({
  scoreboardCoursesGroupedByCategory,
  totalPointsPerCategory,
  completedPointsPerCategory,
  limit = 4,
}) {
  if (
    !(scoreboardCoursesGroupedByCategory instanceof Map) ||
    !(totalPointsPerCategory instanceof Map) ||
    !(completedPointsPerCategory instanceof Map)
  ) {
    return [];
  }

  const numericLimit = Number(limit);

  const normalizedLimit = Number.isFinite(numericLimit)
    ? Math.max(0, Math.floor(numericLimit))
    : 4;

  const orderedCategoryNames = getOrderedCategoryNames(
    scoreboardCoursesGroupedByCategory,
  );

  const incompleteCategories = [];

  for (const categoryName of orderedCategoryNames) {
    const totalPoints = Number(totalPointsPerCategory.get(categoryName));

    if (!Number.isFinite(totalPoints) || totalPoints <= 0) {
      continue;
    }

    const completedProgramPoints =
      completedPointsPerCategory.get(categoryName) || {};

    const hasNoPrograms = Object.keys(completedProgramPoints).length === 0;

    if (hasNoPrograms) {
      continue;
    }

    const completedPoints = sumProgramPoints(completedProgramPoints);

    if (completedPoints < totalPoints) {
      incompleteCategories.push({
        categoryName,
        completedPoints,
        totalPoints,
        remainingPoints: Math.max(0, totalPoints - completedPoints),
        completionPercentage: Math.min(
          100,
          Math.round((completedPoints / totalPoints) * 100),
        ),
      });
    }
  }

  return incompleteCategories.slice(0, normalizedLimit);
}

export function sumProgramPoints(programPoints = {}) {
  if (
    !programPoints ||
    typeof programPoints !== "object" ||
    Array.isArray(programPoints)
  ) {
    return 0;
  }
  return Object.values(programPoints).reduce((total, points) => {
    const numericPoints = Number(points);
    return total + (Number.isFinite(numericPoints) ? numericPoints : 0);
  }, 0);
}
